import MiniHeroSection from "../components/MiniHeroSection";
import ContentBelowHeroBanner from "../components/ContentBelowHeroBanner";
import FaqsSection from "../components/FaqsSection";
import useSWR from "swr";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import {
  secureFetcher,
  SECURITY_CONFIG,
  RateLimiter,
} from "../config/security";

function ContactUs() {
  const [formErrors, setFormErrors] = useState({});
  const rateLimiter = new RateLimiter(SECURITY_CONFIG.RATE_LIMIT_DURATION);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
    form_title: "",
  });

  const contact = "contact";

  useEffect(() => {
    const subject_title =
      contact === "contact" ? "Sky47 Contact Form Submission" : "";

    setFormData((prev) => ({
      ...prev,
      form_title: subject_title,
    }));
  }, [contact]);

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  // Input sanitization function
  const sanitizeInput = (input) => {
    return DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });
  };

  const handleChange = (e) => {
    const sanitizedValue = sanitizeInput(e.target.value);
    setFormData({ ...formData, [e.target.name]: sanitizedValue });
  };

  // Validation
  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+\d{10,15}$/;

    const specialCharRegex = /[^a-zA-Z0-9\s]/;

    const hasSpecialChar = (value) => specialCharRegex.test(value);

    if (!formData.name.trim()) {
      errors.name = "Name is required.";
    } else if (hasSpecialChar(formData.name)) {
      errors.name = "Special characters are not allowed.";
    }

    // Phone
    if (!formData.phone.trim()) {
      errors.phone = "Phone is required.";
    } else if (!phoneRegex.test(formData.phone)) {
      errors.phone =
        "Please start with '+', include country code and add 10 to 15 numbers.";
    }

    // Email
    if (!formData.email.trim()) {
      errors.email = "Email is required.";
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Invalid email format.";
    }

    // Service
    if (!formData.service) {
      errors.service = "Please select a service.";
    } else if (hasSpecialChar(formData.service)) {
      errors.service = "Special characters are not allowed.";
    }

    // Message
    if (!formData.message.trim()) {
      errors.message = "Message is required.";
    } else if (hasSpecialChar(formData.message)) {
      errors.message = "Special characters are not allowed.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    // Rate limiting check
    if (!rateLimiter.canProceed()) {
      const remainingTime = Math.ceil(rateLimiter.getRemainingTime() / 1000);
      setStatus(
        `Please wait ${remainingTime} seconds before submitting again.`
      );
      return;
    }

    const isValid = validateForm();
    if (!isValid) return;

    setLoading(true);

    try {
      const res = await fetch(
        `${SECURITY_CONFIG.API_BASE_URL}${SECURITY_CONFIG.CONTACT_ENDPOINT}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Origin: window.location.origin,
          },
          credentials: "omit", // Don't send cookies for cross-origin requests
          body: JSON.stringify(formData),
        }
      );

      const result = await res.json();
      if (result.success) {
        setStatus("Message sent successfully!");
        setFormData({
          name: "",
          phone: "",
          email: "",
          service: "",
          message: "",
          form_title: "",
        });
        setFormErrors({});
      } else {
        setStatus(
          `Failed to send message. ${result.error ? result.error : ""}`
        );
      }
    } catch (err) {
      setStatus(`Error sending message. ${err.message}`);
    }

    setLoading(false);
  };

  const {
    data: ContactData,
    error,
    isLoading,
  } = useSWR(
    `${SECURITY_CONFIG.API_BASE_URL}${SECURITY_CONFIG.WP_PAGES_ENDPOINT}/171`,
    secureFetcher
  );
  const {
    data: ContactDetailData,
    error: ContactDetailDataError,
    isLoading: ContactDetailDataLoading,
  } = useSWR(
    "https://47.activ8.digital/wp-json/wp/v2/pages/171",
    secureFetcher
  );

  return (
    <>
      {ContactData && (
        <Helmet>
          <title>{ContactData.yoast_head_json?.title}</title>
          <meta
            property="og:title"
            content={ContactData.yoast_head_json?.og_title}
          />
          <meta
            property="og:description"
            content={ContactData.yoast_head_json?.og_description}
          />
        </Helmet>
      )}
      <MiniHeroSection data={ContactData?.acf?.hero_banner} />
      <ContentBelowHeroBanner
        data={ContactData?.acf?.content_below_hero_banner}
      />

      {/* Contact us Details Section start */}
      <div className="contact-main-container contactus_page_detail_sec">
        {/* Left Section - Contact Details */}
        <div className="contact-container">
          <div className="contact-header">
            <h2 className="contact-title">
              {ContactDetailData?.acf?.contact_info_section?.main_heading}
            </h2>
            <p className="contact-description">
              {ContactDetailData?.acf?.contact_info_section?.contact_para}
            </p>
          </div>

          <div className="footer_right contact_page_details">
            <ul className="contact_info">
              <li>
                <a
                  href={
                    ContactDetailData?.acf?.contact_info_section?.email_link
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="19"
                    viewBox="0 0 25 19"
                    fill="none"
                  >
                    <path
                      d="M23.1948 0.276367H1.31651C1.13518 0.276367 0.961281 0.348399 0.833062 0.476617C0.704844 0.604835 0.632812 0.778736 0.632812 0.960065V16.4572C0.632812 16.8803 0.800888 17.2861 1.10006 17.5852C1.39924 17.8845 1.80501 18.0525 2.22811 18.0525H22.2832C22.7063 18.0525 23.1121 17.8845 23.4113 17.5852C23.7104 17.2861 23.8785 16.8803 23.8785 16.4572V0.960065C23.8785 0.778736 23.8065 0.604835 23.6783 0.476617C23.5501 0.348399 23.3761 0.276367 23.1948 0.276367ZM12.2557 10.0601L3.07361 1.64376H21.4377L12.2557 10.0601ZM9.25537 9.16443L2.00021 15.8145V2.51434L9.25537 9.16443ZM10.2672 10.092L11.7999 11.4913C11.9259 11.6066 12.0905 11.6705 12.2614 11.6705C12.4322 11.6705 12.5968 11.6066 12.7229 11.4913L14.2498 10.092L21.4377 16.6851H3.07475L10.2672 10.092ZM15.256 9.16443L22.5111 2.51434V15.8145L15.256 9.16443Z"
                      fill="#28B34B"
                    />
                  </svg>{" "}
                  {ContactDetailData?.acf?.contact_info_section?.email}
                </a>
              </li>
              {/* <li>
                  <a href="tel:+923000990810">
                      <svg xmlns="http://www.w3.org/2000/svg" width="29" height="28" viewBox="0 0 29 28"
                          fill="none">
                          <path
                              d="M18.7085 16.9098C18.8557 16.8118 19.0251 16.752 19.2014 16.7361C19.3775 16.72 19.5549 16.7482 19.7174 16.818L25.9869 19.6271C26.1983 19.7174 26.3746 19.8737 26.4896 20.0726C26.6046 20.2715 26.6521 20.5023 26.625 20.7304C26.4185 22.274 25.6585 23.69 24.4862 24.7151C23.3139 25.7401 21.8091 26.3046 20.2519 26.3033C15.4568 26.3033 10.858 24.3986 7.46739 21.0079C4.07672 17.6173 2.17188 13.0185 2.17188 8.2234C2.17069 6.66613 2.73512 5.16144 3.76021 3.98913C4.78529 2.81683 6.20125 2.05674 7.74477 1.8502C7.97294 1.8231 8.20375 1.87061 8.40266 1.98564C8.60157 2.10067 8.75788 2.27703 8.84818 2.48832L11.6572 8.76314C11.7262 8.9243 11.7543 9.10003 11.739 9.27468C11.7237 9.44933 11.6655 9.61749 11.5695 9.76418L8.72854 13.1422C8.62777 13.2942 8.56817 13.4699 8.55558 13.6518C8.543 13.8339 8.57786 14.016 8.65674 14.1804C9.75616 16.4312 12.0826 18.7298 14.34 19.8185C14.5053 19.8971 14.6883 19.9312 14.8709 19.9174C15.0533 19.9037 15.2292 19.8426 15.3809 19.7401L18.7085 16.9098Z"
                              stroke="#28B34B" strokeWidth="1.82319" strokeLinecap="round"
                              strokeLinejoin="round" />
                      </svg> +92 300-0990810
                  </a>
              </li> */}
              <li>
                <a
                  href={
                    ContactDetailData?.acf?.contact_info_section
                      ?.address_map_link
                  }
                  target="_blank"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="27"
                    viewBox="0 0 22 27"
                    fill="none"
                  >
                    <path
                      d="M11.0419 6.30161C10.1854 6.30161 9.34826 6.55557 8.63619 7.03136C7.92411 7.50715 7.3691 8.18342 7.04138 8.97464C6.71364 9.76586 6.62789 10.6365 6.79497 11.4765C6.96205 12.3164 7.37445 13.088 7.98002 13.6935C8.58559 14.2991 9.35714 14.7115 10.1971 14.8786C11.0371 15.0456 11.9077 14.9599 12.6989 14.6322C13.4901 14.3044 14.1664 13.7494 14.6421 13.0374C15.118 12.3253 15.3719 11.4881 15.3719 10.6317C15.3719 9.48329 14.9157 8.38191 14.1037 7.56986C13.2916 6.75781 12.1903 6.30161 11.0419 6.30161ZM11.0419 13.5943C10.4559 13.5943 9.88308 13.4206 9.39587 13.095C8.90866 12.7695 8.52892 12.3068 8.30468 11.7655C8.08044 11.2241 8.02177 10.6284 8.13608 10.0537C8.25041 9.479 8.53257 8.9511 8.94692 8.53676C9.36126 8.12242 9.88916 7.84025 10.4639 7.72594C11.0386 7.61162 11.6343 7.67029 12.1756 7.89452C12.717 8.11876 13.1797 8.4985 13.5052 8.98571C13.8308 9.47292 14.0045 10.0457 14.0045 10.6317C14.0045 11.4175 13.6924 12.171 13.1368 12.7266C12.5812 13.2822 11.8276 13.5943 11.0419 13.5943ZM11.0419 0.832031C8.44374 0.835047 5.95291 1.86848 4.11577 3.70561C2.27864 5.54276 1.2452 8.03358 1.24219 10.6317C1.24219 14.1539 2.87622 17.8949 5.96768 21.4501C7.363 23.0625 8.93339 24.5149 10.6499 25.7802C10.7648 25.8605 10.9016 25.9037 11.0419 25.9037C11.1821 25.9037 11.3189 25.8605 11.4338 25.7802C13.1503 24.5149 14.7207 23.0625 16.116 21.4501C19.2075 17.8949 20.8415 14.1573 20.8415 10.6317C20.8384 8.03358 19.8051 5.54276 17.9679 3.70561C16.1308 1.86848 13.64 0.835047 11.0419 0.832031ZM11.0419 24.3638C9.33261 23.0442 2.60958 17.4253 2.60958 10.6317C2.60958 8.39532 3.49798 6.25054 5.07933 4.66918C6.6607 3.08782 8.80548 2.19943 11.0419 2.19943C13.2783 2.19943 15.4229 3.08782 17.0044 4.66918C18.5857 6.25054 19.4741 8.39532 19.4741 10.6317C19.4741 17.4253 12.7511 23.0442 11.0419 24.3638Z"
                      fill="#28B34B"
                    />
                  </svg>{" "}
                  {ContactDetailData?.acf?.contact_info_section?.address}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Right Section - Contact Form  */}
        <div className="form-container">
          <form
            id="contactForm"
            onSubmit={(e) => {
              e.preventDefault();
              // Email validation
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(formData.email)) {
                setStatus("Please enter a valid email address.");
                return;
              }
              handleSubmit(e);
            }}
          >
            <div className="form-row">
              <input
                type="hidden"
                name="form_title"
                onChange={handleChange}
                value={formData.form_title}
              />
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-input"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleChange}
                />
                {formErrors.name && (
                  <p className="form-error">{formErrors.name}</p>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  Ph. Number
                </label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  className="form-input"
                  placeholder="Enter phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  // pattern="^\+?\d{10,15}$"
                  title="Please enter a valid phone number (10-15 digits, may start with +)"
                />
                {formErrors.phone && (
                  <p className="form-error">{formErrors.phone}</p>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-input"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                />
                {formErrors.email && (
                  <p className="form-error">{formErrors.email}</p>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="service" className="form-label">
                  Select Service
                </label>
                <select
                  id="service"
                  name="service"
                  className="form-select"
                  value={formData.service}
                  onChange={handleChange}
                >
                  <option value="">Choose Services</option>
                  <option value="colocation">Colocation</option>
                  <option value="cloud">Cloud</option>
                  <option value="managed security">Managed Security</option>
                  <option value="GPUaaS">GPUaaS</option>
                </select>
                {formErrors.service && (
                  <p className="form-error">{formErrors.service}</p>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group message-group">
                <label htmlFor="message" className="form-label">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  className="form-textarea"
                  placeholder="Briefly describe your need or question…"
                  value={formData.message}
                  onChange={handleChange}
                ></textarea>
                {formErrors.message && (
                  <p className="form-error">{formErrors.message}</p>
                )}
              </div>
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? "Sending..." : "Submit"}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 20 20"
                fill="none"
              >
                <rect
                  x="0.5"
                  y="0.5"
                  width="19"
                  height="19"
                  rx="9.5"
                  stroke="black"
                />
                <path
                  d="M13.5211 12.7727L13.298 7.10815C13.2915 7.00261 13.2643 6.89775 13.2179 6.79958C13.129 6.60182 12.9682 6.44104 12.7705 6.35212C12.6723 6.30574 12.5675 6.27855 12.4619 6.27209L6.79738 6.04892C6.69175 6.04476 6.58797 6.06145 6.49198 6.09803C6.39598 6.1346 6.30964 6.19036 6.23789 6.26211C6.09299 6.40701 6.0163 6.60827 6.02471 6.8216C6.03311 7.03493 6.12592 7.24286 6.28271 7.39965C6.4395 7.55644 6.64743 7.64925 6.86076 7.65765L10.5935 7.79906L5.91933 12.4732C5.77502 12.6175 5.69864 12.8179 5.70701 13.0304C5.71539 13.2429 5.80781 13.4499 5.96396 13.6061C6.12012 13.7623 6.3272 13.8547 6.53966 13.863C6.75212 13.8714 6.95256 13.795 7.09687 13.6507L11.771 8.9766L11.9124 12.7093C11.9161 12.815 11.9408 12.9206 11.9849 13.02C12.029 13.1194 12.0918 13.2106 12.1695 13.2883C12.2472 13.366 12.3384 13.4287 12.4378 13.4729C12.5371 13.517 12.6427 13.5416 12.7485 13.5454C12.8542 13.55 12.9582 13.5336 13.0544 13.4972C13.1506 13.4607 13.2371 13.405 13.309 13.3332C13.3808 13.2613 13.4365 13.1748 13.4729 13.0786C13.5094 12.9824 13.5257 12.8785 13.5211 12.7727Z"
                  fill="black"
                />
              </svg>
            </button>

            {status && (
              <p
                style={{
                  marginTop: "10px",
                  color: status.includes("successfully") ? "green" : "red",
                }}
              >
                {status}
              </p>
            )}
          </form>
        </div>
      </div>
      <FaqsSection data={ContactDetailData?.acf?.contact_faq_section} />
    </>
  );
}
export default ContactUs;
